import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Table, Tag, Button, Space, Select, Modal, Input, App, Card, Typography, Tooltip } from 'antd';
import { ReloadOutlined, FileImageOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { get, patch } from '../../api/client';
import { ProtectedImage } from '../../components/ProtectedImage';

interface ManualApplication {
  id: string;
  username: string;
  fullName: string;
  phone: string;
  backupPhone?: string;
  idCard: string;
  idCardPhoto?: string;
  email?: string;
  status: string;
  remark?: string;
  rejectReason?: string;
  reviewNote?: string;
  processedAt?: string;
  createdAt: string;
}

interface ApprovalsResponse {
  items: ManualApplication[];
  total: number;
  page: number;
  pageSize: number;
}

const statusTag: Record<string, { color: string; label: string }> = {
  pending: { color: 'gold', label: '待审核' },
  approved: { color: 'green', label: '已通过' },
  rejected: { color: 'red', label: '已驳回' },
};

export default function ApprovalsPage() {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState('pending');
  const [page, setPage] = useState(1);
  const [dialog, setDialog] = useState<{ type: 'approve' | 'reject'; id: string } | null>(null);
  const [remark, setRemark] = useState('');
  /** 已按需揭示的身份证全号（rowId → 全号）；列表本身只下发掩码 */
  const [revealed, setRevealed] = useState<Record<string, string>>({});

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['admin-approvals', status, page],
    queryFn: () => get<ApprovalsResponse>('/admin/approvals', { status, page, pageSize: 20 }),
  });

  const reveal = useMutation({
    mutationFn: (id: string) => get<{ idCard: string }>(`/admin/approvals/${id}/id-card`),
    onSuccess: (res, id) => setRevealed((prev) => ({ ...prev, [id]: res.idCard })),
    onError: (error) => {
      message.error(error instanceof Error ? error.message : '获取身份证号失败');
    },
  });

  const act = useMutation({
    mutationFn: ({ id, type, remark }: { id: string; type: 'approve' | 'reject'; remark: string }) =>
      type === 'approve'
        ? patch(`/admin/approvals/${id}/approve`, { remark })
        : patch(`/admin/approvals/${id}/reject`, { reason: remark }),
    onSuccess: (_data, vars) => {
      if (vars.type === 'approve') {
        const pwd = (_data as any)?.generatedPassword;
        message.success(pwd ? `审核通过，已创建账号并发送通知；初始密码：${pwd}` : '审核通过成功');
      } else {
        message.success('驳回成功，已通过邮件/短信通知申请人（如已配置）');
      }
      setDialog(null);
      setRemark('');
      setRevealed({});
      void queryClient.invalidateQueries({ queryKey: ['admin-approvals'] });
    },
    onError: (error) => {
      message.error(error instanceof Error ? error.message : '操作失败');
    },
  });

  const rows = data?.items ?? [];
  const total = data?.total ?? 0;

  const columns = [
    { title: '用户名', dataIndex: 'username', width: 130 },
    { title: '姓名', dataIndex: 'fullName', width: 100 },
    { title: '手机号', dataIndex: 'phone', width: 125 },
    {
      title: '身份证号',
      dataIndex: 'idCard',
      width: 205,
      render: (v: string, record: ManualApplication) => {
        const full = revealed[record.id];
        return (
          <Space size={4}>
            <span>{full ?? v ?? '-'}</span>
            {full ? (
              <Button
                type="link"
                size="small"
                style={{ padding: 0 }}
                onClick={() =>
                  setRevealed((prev) => {
                    const next = { ...prev };
                    delete next[record.id];
                    return next;
                  })
                }
              >
                收起
              </Button>
            ) : (
              v && (
                <Button
                  type="link"
                  size="small"
                  style={{ padding: 0 }}
                  loading={reveal.isPending && reveal.variables === record.id}
                  onClick={() => reveal.mutate(record.id)}
                >
                  查看
                </Button>
              )
            )}
          </Space>
        );
      },
    },
    {
      title: '身份证照片',
      dataIndex: 'idCardPhoto',
      width: 100,
      render: (v: string, record: ManualApplication) =>
        v ? (
          <ProtectedImage
            src={v}
            width={44}
            height={32}
            style={{ objectFit: 'cover', borderRadius: 4 }}
            fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg=="
          />
        ) : (
          // 空值有两种来历，必须分开说：不然审核人会以为是上传失败而去找申请人重传
          <span style={{ color: '#999', fontSize: 12 }}>
            {record.status === 'rejected' ? '驳回后已销毁' : '未上传'}
          </span>
        ),
    },
    { title: '邮箱', dataIndex: 'email', width: 170, render: (v: string) => v || '-' },
    {
      title: '申请说明',
      dataIndex: 'remark',
      width: 160,
      render: (v: string) =>
        v ? (
          <Tooltip title={v}>
            <Typography.Text ellipsis style={{ maxWidth: 150 }}>
              {v}
            </Typography.Text>
          </Tooltip>
        ) : (
          '-'
        ),
    },
    {
      title: '驳回理由',
      dataIndex: 'rejectReason',
      width: 150,
      render: (v: string) =>
        v ? (
          <Tooltip title={v}>
            <Typography.Text type="danger" ellipsis style={{ maxWidth: 140 }}>
              {v}
            </Typography.Text>
          </Tooltip>
        ) : (
          '-'
        ),
    },
    {
      title: '审核备注',
      dataIndex: 'reviewNote',
      width: 140,
      render: (v: string) =>
        v ? (
          <Tooltip title={v}>
            <Typography.Text ellipsis style={{ maxWidth: 130 }}>
              {v}
            </Typography.Text>
          </Tooltip>
        ) : (
          '-'
        ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 90,
      render: (v: string) => {
        const tag = statusTag[v] ?? { color: 'default', label: v };
        return <Tag color={tag.color}>{tag.label}</Tag>;
      },
    },
    {
      title: '申请时间',
      dataIndex: 'createdAt',
      width: 165,
      render: (v: string) => (v ? dayjs(v).format('YYYY-MM-DD HH:mm:ss') : '-'),
    },
    {
      title: '操作',
      key: 'action',
      width: 160,
      fixed: 'right' as const,
      render: (_: unknown, record: ManualApplication) =>
        record.status === 'pending' ? (
          <Space>
            <Button
              type="primary"
              size="small"
              onClick={() => {
                setRemark('');
                setDialog({ type: 'approve', id: record.id });
              }}
            >
              通过
            </Button>
            <Button
              danger
              size="small"
              onClick={() => {
                setRemark('');
                setDialog({ type: 'reject', id: record.id });
              }}
            >
              驳回
            </Button>
          </Space>
        ) : (
          <Typography.Text type="secondary">
            已处理{record.processedAt ? ` ${dayjs(record.processedAt).format('MM-DD HH:mm')}` : ''}
          </Typography.Text>
        ),
    },
  ];

  return (
    <Card
      title="人工注册审核"
      extra={
        <Space>
          <Select
            value={status}
            style={{ width: 140 }}
            onChange={(v) => {
              setStatus(v);
              setPage(1);
              setRevealed({});
            }}
            options={[
              { value: 'all', label: '全部' },
              { value: 'pending', label: '待审核' },
              { value: 'approved', label: '已通过' },
              { value: 'rejected', label: '已驳回' },
            ]}
          />
          <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isFetching}>
            刷新
          </Button>
        </Space>
      }
    >
      <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
        <FileImageOutlined style={{ marginRight: 6 }} />
        通过后将自动创建账号，并通过邮件发送账号与初始密码、短信通知（需在「系统设置-短信服务」开启短信通知）；
        驳回后将通过邮件告知驳回理由，申请人可修正信息后重新提交。
      </Typography.Paragraph>
      <Table<ManualApplication>
        rowKey="id"
        loading={isLoading}
        dataSource={rows}
        scroll={{ x: 1600 }}
        pagination={{
          current: page,
          pageSize: 20,
          total,
          showTotal: (t) => `共 ${t} 条`,
          onChange: (p) => setPage(p),
        }}
        columns={columns}
      />

      <Modal
        title={dialog?.type === 'approve' ? '确认通过' : '确认驳回'}
        open={dialog !== null}
        onCancel={() => setDialog(null)}
        onOk={() => {
          if (!dialog) return;
          if (dialog.type === 'reject' && !remark.trim()) {
            message.warning('驳回理由必填');
            return;
          }
          act.mutate({ id: dialog.id, type: dialog.type, remark });
        }}
        confirmLoading={act.isPending}
        okText={dialog?.type === 'approve' ? '确认通过' : '确认驳回'}
        okButtonProps={{ danger: dialog?.type === 'reject' }}
      >
        {dialog?.type === 'approve' ? (
          <Typography.Paragraph type="warning">
            确认通过后系统将自动创建正式账号：账号信息与初始密码通过邮件发送，短信通知受后台开关控制。
          </Typography.Paragraph>
        ) : null}
        <Input.TextArea
          value={remark}
          onChange={(e) => setRemark(e.target.value)}
          rows={3}
          maxLength={500}
          showCount
          placeholder={dialog?.type === 'approve' ? '审核备注（可选）' : '驳回理由（必填，将通过邮件告知申请人）'}
        />
      </Modal>
    </Card>
  );
}
